import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Container, Loader2, MapPin, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

const COLS = "grid-cols-[130px_120px_90px_80px_minmax(170px,1fr)_100px_minmax(240px,1.4fr)_minmax(240px,1.4fr)]";

export const ago = (iso) => {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  return m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};

export const Stat = ({ label, value, testId, tone = "text-slate-100" }) => (
  <div data-testid={testId} className="rounded-xl border border-slate-800 bg-slate-900/70 px-4 py-3">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`mt-1 text-2xl font-extrabold ${tone}`}>{value}</p>
  </div>
);

export const GpsCell = ({ g, testId }) => (
  <span data-testid={testId} className="flex min-w-0 items-center gap-1.5 text-slate-300" title={g.location}>
    <MapPin className={`h-3.5 w-3.5 shrink-0 ${g.speed_mph > 0 ? "text-emerald-400" : "text-slate-500"}`} />
    <span className="truncate">{g.location || `${g.lat?.toFixed(3)}, ${g.lng?.toFixed(3)}`}</span>
    <span className="shrink-0 text-[10px] text-slate-500">{g.speed_mph > 0 ? `${g.speed_mph} mph · ` : ""}{ago(g.time)}</span>
  </span>
);

export const idleInfo = (t, n) => {
  const why = [];
  if (t.days_no_load == null) why.push("no load on record");
  else if (t.days_no_load >= n) why.push(`no load for ${t.days_no_load} days (last load ended ${t.last_busy})`);
  if (t.samsara && !t.stale && t.days_not_moved != null && t.days_not_moved >= n) why.push(`not moved for ${t.not_moved_at_least ? "at least " : ""}${t.days_not_moved} days`);
  if (!why.length) return null;
  if (t.days_no_load == null && !(t.samsara && !t.stale && t.days_not_moved >= n)) return { label: "Idle · no loads", why: why.join(" · ") };
  const days = Math.max(t.days_no_load ?? 0, t.samsara && !t.stale ? t.days_not_moved ?? 0 : 0);
  return { label: `Idle ${days} days`, why: why.join(" · ") };
};

const IdleTag = ({ t, n }) => {
  const i = idleInfo(t, n);
  return i && <span data-testid={`trailer-idle-${t.trailer_num}`} title={i.why}
    className="w-fit rounded-full border border-red-500/40 bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold text-red-300">{i.label}</span>;
};

const Row = ({ t, n }) => (
  <div data-testid={`trailer-row-${t.trailer_num}`} className={`grid ${COLS} min-w-[1300px] items-center gap-3 border-b border-slate-800/50 px-4 py-2 text-xs transition-colors hover:bg-slate-800/30 ${t.mismatch ? "bg-red-500/5" : ""}`}>
    <span className="flex flex-col gap-1">
      <span className="flex items-center gap-1.5 font-mono font-bold text-blue-300"><Container className="h-3.5 w-3.5 text-slate-500" />#{t.trailer_num}</span>
      <IdleTag t={t} n={n} />
    </span>
    <span className="truncate text-slate-300">{[t.make, t.year].filter(Boolean).join(" · ") || "—"}</span>
    <span className="truncate text-slate-300">{[t.equipment, t.size].filter(Boolean).join(" ") || "—"}</span>
    <span className={(t.status || "").toLowerCase() === "active" ? "text-emerald-300" : "text-slate-500"}>{t.status || "—"}</span>
    <span className="min-w-0 truncate text-slate-200" title={t.load_number ? `Load ${t.load_number}` : ""}>
      {t.last_known && <span data-testid={`trailer-last-known-${t.trailer_num}`} className="mr-1 text-[10px] uppercase text-amber-300/80">last known</span>}
      {t.driver || "—"}{t.truck_num && <span className="text-slate-500"> · truck <span className="font-mono text-blue-300">#{t.truck_num}</span></span>}
    </span>
    <span className="text-slate-400">{t.last_load || "—"}</span>
    {t.samsara ? <span className="flex min-w-0 flex-col gap-0.5"><GpsCell g={t.samsara} testId={`trailer-gps-${t.trailer_num}`} />
      {t.stale && <span className="text-[10px] text-amber-300/80">tracker not reporting for 2+ days</span>}</span>
      : <span className="text-slate-600">No tracker</span>}
    {t.truck_gps ? (
      <span className="flex min-w-0 flex-col gap-0.5">
        <GpsCell g={t.truck_gps} />
        {t.distance_mi != null && (t.mismatch
          ? <span data-testid={`trailer-mismatch-${t.trailer_num}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-300"><AlertTriangle className="h-3 w-3" />{t.distance_mi.toLocaleString()} mi from the trailer</span>
          : t.distance_mi <= 25 ? <span className="text-[10px] text-emerald-400/80">with the trailer ({t.distance_mi} mi)</span>
            : <span className="text-[10px] text-slate-500">{t.distance_mi.toLocaleString()} mi apart · {t.stale ? "tracker stale" : "last trip over 10 days ago"}</span>)}
      </span>
    ) : <span className="text-slate-600">{t.truck_num ? "Truck not in Samsara" : "—"}</span>}
  </div>
);

export const FleetTrailers = () => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [mismatchOnly, setMismatchOnly] = useState(false);
  const [idleOnly, setIdleOnly] = useState(false);
  const [idleDays, setIdleDays] = useState(() => Number(localStorage.getItem("trailerIdleDays")) || 3);
  const changeIdle = (v) => { const n = Math.max(1, Math.min(120, Number(v) || 1)); setIdleDays(n); localStorage.setItem("trailerIdleDays", n); };
  useEffect(() => {
    let timer, started = false;
    const load = () => api.get("/ops/fleet/trailers").then((r) => {
      setData(r.data);
      if (r.data.trips_synced) return;
      if (!started) { started = true; api.post("/alvys/sync-historical").catch(() => {}); }
      timer = setTimeout(load, 60000);
    }).catch(() => { toast.error("Failed to load trailers"); setData({ trailers: [] }); });
    load();
    return () => clearTimeout(timer);
  }, []);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.trailers || []).filter((t) => (!activeOnly || (t.status || "").toLowerCase() === "active") && (!mismatchOnly || t.mismatch) && (!idleOnly || idleInfo(t, idleDays))
      && (!q || [t.trailer_num, t.make, t.equipment, t.driver, t.truck_num, t.samsara?.location, t.truck_gps?.location].join(" ").toLowerCase().includes(q)));
  }, [data, search, activeOnly, mismatchOnly, idleOnly, idleDays]);
  if (!data) return <div className="flex items-center justify-center gap-2 py-24 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" />Loading trailers from Alvys and Samsara…</div>;
  const all = data.trailers;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Trailers in Alvys" value={all.length} testId="trailers-total" />
        <Stat label="Active" value={all.filter((t) => (t.status || "").toLowerCase() === "active").length} testId="trailers-active" />
        <Stat label="With Samsara Tracker" value={all.filter((t) => t.samsara).length} testId="trailers-tracked" />
        <Stat label="Away From Truck" value={data.mismatches} testId="trailers-mismatch" tone={data.mismatches ? "text-red-300" : "text-slate-100"} />
      </div>
      {!data.trips_synced && <p data-testid="trailers-sync-note" className="inline-flex items-center gap-2 text-xs text-amber-300"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading trailer assignments from Alvys trips. This takes a few minutes; the list updates by itself.</p>}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input data-testid="trailers-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Trailer #, driver, truck, location…"
            className="h-9 w-[300px] border-slate-700/50 bg-slate-800/50 pl-9 text-slate-200" />
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-slate-300"><Switch data-testid="trailers-active-only" checked={activeOnly} onCheckedChange={setActiveOnly} />Active only</label>
        <label className="inline-flex items-center gap-2 text-xs text-slate-300"><Switch data-testid="trailers-mismatch-only" checked={mismatchOnly} onCheckedChange={setMismatchOnly} />Away from truck only</label>
        <label className="inline-flex items-center gap-2 text-xs text-slate-300"><Switch data-testid="trailers-idle-only" checked={idleOnly} onCheckedChange={setIdleOnly} />Idle only</label>
        <label className="inline-flex items-center gap-2 text-xs text-slate-300">Idle after
          <Input data-testid="trailers-idle-days" type="number" min={1} max={120} value={idleDays} onChange={(e) => changeIdle(e.target.value)}
            className="h-8 w-16 border-slate-700/50 bg-slate-800/50 text-center text-slate-200" />days</label>
        <span data-testid="trailers-count" className="text-xs text-slate-400">{rows.length} trailers</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/70">
        <div className={`grid ${COLS} min-w-[1300px] gap-3 border-b border-slate-800 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500`}>
          <span>Trailer</span><span>Make · Year</span><span>Equip</span><span>Status</span><span>Current Driver · Truck</span><span>Last Pickup</span><span>Trailer Location</span><span>Truck Location</span>
        </div>
        {rows.map((t) => <Row key={t.trailer_id} t={t} n={idleDays} />)}
      </div>
    </>
  );
};
